import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import { DiscoveryFrameworksScreen } from '@/components/discovery-frameworks/discovery-frameworks-screen';
import {
  parseFrameworkSearch,
  type FrameworkSearch,
  type FrameworkTab,
} from '@/components/discovery-frameworks/framework-logic';

/**
 * Firm settings → Discovery frameworks (T-0184 / E12-F01).
 *
 * The firm's discovery method: areas, interview types, questionnaires,
 * rules and history, read by every firm user and curated through a draft
 * and a publish. Design reference: the `#frameworks` screen of
 * `docs/discovery/prototype/discovery.js`. The screen is
 * `components/discovery-frameworks/discovery-frameworks-screen.tsx`; this
 * route owns the URL state so a view can be shared:
 *
 *   ?framework=<framework id>&tab=areas|types|questionnaires|rules|history
 *   &process=<process id>   a reference process map, open in place of the areas (T-0189)
 */

export const Route = createFileRoute('/settings/discovery-frameworks')({
  validateSearch: (search: Record<string, unknown>): FrameworkSearch =>
    parseFrameworkSearch(search),
  component: DiscoveryFrameworksRoute,
});

function DiscoveryFrameworksRoute() {
  const { framework, tab, process } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const onFrameworkChange = useCallback(
    (id: string) => void navigate({ search: { framework: id } }),
    [navigate],
  );
  const onTabChange = useCallback(
    (next: FrameworkTab) =>
      void navigate({
        search: (prev) => ({
          ...prev,
          tab: next === 'areas' ? undefined : next,
          process: undefined,
        }),
        replace: true,
      }),
    [navigate],
  );
  const onProcessChange = useCallback(
    (id: string | null) =>
      void navigate({ search: (prev) => ({ ...prev, process: id ?? undefined }) }),
    [navigate],
  );

  return (
    // Fills the settings pane: viewport minus the 56px top bar and the
    // settings layout's vertical padding.
    <div className="h-[calc(100vh-56px-48px)] min-h-[480px] overflow-hidden rounded-card border border-ink-100 bg-paper-0">
      <DiscoveryFrameworksScreen
        frameworkId={framework ?? null}
        tab={tab ?? 'areas'}
        processId={process ?? null}
        onFrameworkChange={onFrameworkChange}
        onTabChange={onTabChange}
        onProcessChange={onProcessChange}
      />
    </div>
  );
}
